import { useCallback, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { encodeSkillPromptToAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useActiveAction } from '@atlassian/conversation-assistant/controllers/active-action';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant/types';
import {
	type ConversationAction,
	HandleActionType,
	LEGACY_DYNAMIC_SKILL_IDS,
} from '@atlassian/rovo-conversation-actions/types';
import {
	type RovoExperienceSidebarAPI,
	useRovoExperienceAPIStore,
} from '@atlassian/rovo-experience-api/rovo-experience-api-store';

export const useRovoActionTrigger = (
	sidebarApi: RovoExperienceSidebarAPI,
	product: string,
): {
	triggerAction: (action: ConversationAction, mode?: ConversationAssistantUIModes) => void;
} => {
	di(useActiveAction, usePublish, useRovoExperienceAPIStore);

	const { setActiveAction, activeAction } = useActiveAction();
	const publish = usePublish('ai-mate');
	const {
		isMiniModalAvailable,
		isFullScreenModalAvailable,
		toggleConversationAssistantUIMode,
		mode: currentMode,
	} = useRovoExperienceAPIStore();

	const triggerAction = useCallback(
		(action: ConversationAction, mode?: ConversationAssistantUIModes) => {
			if (action.onClick) {
				action.onClick();
				return;
			}

			const setNewActiveAction = (actionToSet: ConversationAction) => {
				switch (actionToSet.handleType) {
					case HandleActionType.Nested:
						return;
					case HandleActionType.Skill:
						if ('staticPrompt' in actionToSet) {
							publish({
								type: 'chat-new',
								product,
								source: 'rovo-action-trigger',
								openChat: false,
								data: {
									prompt: actionToSet.staticPrompt
										? encodeSkillPromptToAdf(actionToSet.staticPrompt).adf
										: '',
									dialogues: [],
								},
								openChatMode: mode ?? 'mini-modal',
							});
							return;
						}

						if (
							'dynamicPrompt' in actionToSet &&
							LEGACY_DYNAMIC_SKILL_IDS.has(actionToSet.id) &&
							fg('fix_dynamic_prompts_in_rovo_action_trigger')
						) {
							publish({
								type: 'insert-prompt',
								product,
								source: 'rovo-action-trigger',
								openChat: false,
								data: {
									prompt: actionToSet.dynamicPrompt ?? '',
									placeholderType: 'skill',
									overrideAutoSend: true,
								},
								openChatMode: mode ?? 'mini-modal',
							});
							return;
						}

						break;
					case HandleActionType.Chat:
						if ('staticPrompt' in actionToSet) {
							publish({
								type: 'chat-new',
								product,
								source: 'rovo-action-trigger',
								openChat: false,
								data: {
									prompt: actionToSet.staticPrompt ?? '',
									dialogues: [],
								},
								openChatMode: mode ?? 'mini-modal',
							});
							return;
						}

						if (
							'dynamicPrompt' in actionToSet &&
							fg('fix_dynamic_prompts_in_rovo_action_trigger')
						) {
							const actionFiles =
								'uploadedFiles' in actionToSet ? actionToSet.uploadedFiles : undefined;
							const actionPromptAdf =
								'dynamicPromptAdf' in actionToSet ? actionToSet.dynamicPromptAdf : undefined;
							publish({
								type: 'insert-prompt',
								product,
								source: 'rovo-action-trigger',
								openChat: false,
								data: {
									prompt: actionToSet.dynamicPrompt ?? '',
									overrideAutoSend: true,
									...(actionPromptAdf ? { promptAdf: actionPromptAdf } : {}),
									...(actionFiles?.length ? { files: actionFiles } : {}),
									...(actionToSet.encodeDynamicPromptAsSkill ? { placeholderType: 'skill' } : {}),
								},
								openChatMode: mode ?? 'mini-modal',
							});
							return;
						}

						break;
					case HandleActionType.Base:
						if (actionToSet.viewConfig) {
							setActiveAction(actionToSet);
						}
						break;
					default:
						break;
				}
			};

			const targetMode = fg('agent_session_experience_fg')
				? calculateTargetMode(isMiniModalAvailable, isFullScreenModalAvailable, mode)
				: calculateTargetMode(isMiniModalAvailable, undefined, mode);

			//If there's an existing active action or the target mode is different from the current mode, set to undefined first and/or set the new mode first
			if (activeAction || targetMode !== currentMode) {
				if (activeAction) {
					setActiveAction(undefined);
				}
				if (targetMode !== currentMode) {
					toggleConversationAssistantUIMode({
						targetMode,
						sidebarApi,
					});
				}
				setTimeout(() => {
					setNewActiveAction(action);
				}, 0);
				return;
			}
			setNewActiveAction(action);
		},
		[
			setActiveAction,
			activeAction,
			toggleConversationAssistantUIMode,
			isMiniModalAvailable,
			isFullScreenModalAvailable,
			sidebarApi,
			currentMode,
			product,
			publish,
		],
	);

	return useMemo(
		() => ({
			triggerAction,
		}),
		[triggerAction],
	);
};

const calculateTargetMode = (
	isMiniModalAvailable: boolean,
	isFullScreenModalAvailable?: boolean,
	requestedMode?: ConversationAssistantUIModes,
): ConversationAssistantUIModes => {
	// Full-screen modal is opt-in via FG and only honoured when the host has declared availability.
	// If either is missing, fall back to the standard mini-modal/sidebar precedence.
	if (
		requestedMode === ConversationAssistantUIModes.FULL_SCREEN_MODAL &&
		(!isFullScreenModalAvailable || !fg('agent_session_experience_fg'))
	) {
		return isMiniModalAvailable
			? ConversationAssistantUIModes.MINI_MODAL
			: ConversationAssistantUIModes.SIDEBAR;
	}

	if (requestedMode === ConversationAssistantUIModes.MINI_MODAL && !isMiniModalAvailable) {
		return ConversationAssistantUIModes.SIDEBAR;
	}

	if (requestedMode) {
		return requestedMode;
	}

	return isMiniModalAvailable
		? ConversationAssistantUIModes.MINI_MODAL
		: ConversationAssistantUIModes.SIDEBAR;
};
